import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Space,
  message,
  Spin,
  Typography,
  Tag,
  Alert,
  Avatar,
  Empty,
  Divider,
  Badge,
  Modal,
} from 'antd';
import {
  CrownOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  StarOutlined,
  GiftOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import llmService from '../../services/llmService';
import { formatMoney, PLATFORM_CURRENCY } from '../../../shared/utils/money';

const { Text, Title } = Typography;

// 配额转换为美元
const quotaToDollar = (quota) => {
  if (quota === undefined || quota === null) return '$0.00';
  const dollars = quota / 500000;
  if (dollars >= 1000) return `$${(dollars / 1000).toFixed(2)}K`;
  if (dollars >= 0.01) return `$${dollars.toFixed(2)}`;
  return `$${dollars.toFixed(4)}`;
};

const planColors = ['#1890ff', '#722ed1', '#fa8c16', '#52c41a', '#eb2f96'];

const Subscription = () => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, subRes] = await Promise.allSettled([
        llmService.getSubscriptionPlans(),
        llmService.getSubscription(),
      ]);

      if (plansRes.status === 'fulfilled' && plansRes.value?.success) {
        setPlans(plansRes.value.data || []);
      }

      if (subRes.status === 'fulfilled' && subRes.value?.success) {
        setSubscription(subRes.value.data);
      }

      if (plansRes.status === 'rejected' && subRes.status === 'rejected') {
        message.error(t('Failed to retrieve subscription information. Please try again later'));
      }
    } catch (error) {
      console.error('获取订阅信息失败:', error);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 订阅暂无自助下单端点，引导用户联系管理员开通
  const handlePlanInquiry = (plan) => {
    Modal.info({
      title: plan.name || plan.title || t('Subscription plan'),
      content: (
        <div>
          {plan.description && <p style={{ marginBottom: 8 }}>{plan.description}</p>}
          <p style={{ marginBottom: 8 }}>
            {t('Price: {{price}}{{quotaInfo}}', { price: plan.price !== undefined ? formatMoney(plan.price, plan.currency || PLATFORM_CURRENCY) : t('Free'), quotaInfo: plan.quota ? t(', monthly quota: {{quota}}', { quota: quotaToDollar(plan.quota) }) : '' })}
          </p>
          <p style={{ marginBottom: 0, color: '#888' }}>
            {t('Online self-service activation is not currently available for this subscription plan. Please contact an administrator to activate or change it.')}
          </p>
        </div>
      ),
      okText: t('Got it'),
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 20, fontWeight: 600 }}>
          <CrownOutlined style={{ marginRight: 8 }} />
          {t('Subscriptions')}
        </Text>
      </div>

      {/* 当前订阅状态 */}
      {subscription && subscription.plan_id ? (
        <Card
          style={{
            borderRadius: 12,
            marginBottom: 16,
            background: 'linear-gradient(135deg, #f9f0ff 0%, #efdbff 100%)',
          }}
        >
          <Row align="middle" gutter={16}>
            <Col>
              <Avatar
                size={56}
                style={{ backgroundColor: '#722ed1' }}
                icon={<CrownOutlined />}
              />
            </Col>
            <Col flex={1}>
              <Space direction="vertical" size={0}>
                <Space>
                  <Title level={4} style={{ margin: 0 }}>
                    {subscription.plan_name || t('Current subscription')}
                  </Title>
                  <Tag color="purple">{t('Taking effect')}</Tag>
                </Space>
                <Text type="secondary">
                  {subscription.expires_at
                    ? t('Valid until {{date}}', { date: new Date(subscription.expires_at * 1000).toLocaleDateString('zh-CN') })
                    : t('Valid indefinitely')}
                </Text>
              </Space>
            </Col>
            <Col>
              <Space direction="vertical" align="end" size={0}>
                <Text type="secondary">{t('Monthly quota')}</Text>
                <Text strong style={{ fontSize: 20, color: '#722ed1' }}>
                  {quotaToDollar(subscription.quota || 0)}
                </Text>
              </Space>
            </Col>
          </Row>
        </Card>
      ) : (
        <Alert
          message={t('No active subscriptions')}
          description={t('You have no active subscriptions. Choose a plan below to get started.')}
          type="info"
          showIcon
          icon={<CrownOutlined />}
          style={{ marginBottom: 16, borderRadius: 8 }}
        />
      )}

      {/* 订阅计划列表 */}
      {plans.length > 0 ? (
        <Row gutter={[16, 16]}>
          {plans.map((plan, idx) => {
            const color = planColors[idx % planColors.length];
            const isCurrentPlan = subscription?.plan_id === plan.id;

            return (
              <Col xs={24} sm={12} lg={8} key={plan.id || idx}>
                <Badge.Ribbon
                  text={isCurrentPlan ? t('Current configuration') : (idx === 1 ? t('Recommended') : null)}
                  color={isCurrentPlan ? 'green' : 'blue'}
                  style={{ display: isCurrentPlan || idx === 1 ? 'block' : 'none' }}
                >
                  <Card
                    hoverable
                    style={{
                      borderRadius: 12,
                      height: '100%',
                      border: isCurrentPlan ? `2px solid ${color}` : undefined,
                    }}
                  >
                    <div style={{ textAlign: 'center', marginBottom: 16 }}>
                      <Avatar
                        size={48}
                        style={{ backgroundColor: color, marginBottom: 8 }}
                        icon={<StarOutlined />}
                      />
                      <Title level={4} style={{ margin: '8px 0 4px' }}>
                        {plan.name || plan.title}
                      </Title>
                      <Text type="secondary">{plan.description || ''}</Text>
                    </div>

                    <div style={{
                      textAlign: 'center',
                      padding: '16px 0',
                      borderTop: '1px solid #f0f0f0',
                      borderBottom: '1px solid #f0f0f0',
                      marginBottom: 16,
                    }}>
                      <Text style={{ fontSize: 32, fontWeight: 700, color }}>
                        {plan.price !== undefined ? formatMoney(plan.price, plan.currency || PLATFORM_CURRENCY) : t('Free')}
                      </Text>
                      {plan.period && (
                        <Text type="secondary" style={{ display: 'block', fontSize: 13 }}>
                          /{plan.period === 'monthly' ? t('Month') : plan.period === 'yearly' ? t('Year') : plan.period}
                        </Text>
                      )}
                    </div>

                    <Space direction="vertical" style={{ width: '100%' }} size="small">
                      {plan.quota && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Space>
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            <Text>{t('Quota')}</Text>
                          </Space>
                          <Text strong>{quotaToDollar(plan.quota)}</Text>
                        </div>
                      )}
                      {plan.group && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Space>
                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            <Text>{t('User group')}</Text>
                          </Space>
                          <Tag color="blue">{plan.group}</Tag>
                        </div>
                      )}
                      {plan.features && plan.features.map((feature, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <CheckCircleOutlined style={{ color: '#52c41a' }} />
                          <Text>{feature}</Text>
                        </div>
                      ))}
                    </Space>

                    <Divider style={{ margin: '16px 0 12px' }} />

                    <Button
                      type={isCurrentPlan ? 'default' : 'primary'}
                      block
                      disabled={isCurrentPlan}
                      onClick={isCurrentPlan ? undefined : () => handlePlanInquiry(plan)}
                      style={{ borderRadius: 8 }}
                    >
                      {isCurrentPlan ? t('Current configuration') : t('Learn more')}
                    </Button>
                  </Card>
                </Badge.Ribbon>
              </Col>
            );
          })}
        </Row>
      ) : (
        <Card style={{ borderRadius: 12 }}>
          <Empty description={t('No subscription plans available')} />
        </Card>
      )}

      {/* 说明 */}
      <Card
        style={{ borderRadius: 12, marginTop: 16 }}
        title={
          <Space>
            <SafetyCertificateOutlined style={{ color: '#1890ff' }} />
            <span>{t('About subscriptions')}</span>
          </Space>
        }
      >
        <ul style={{ margin: 0, paddingLeft: 20, color: '#666' }}>
          <li>{t('Subscription plans provide a fixed monthly quota and higher user group privileges')}</li>
          <li>{t('Subscription quotas reset automatically each month')}</li>
          <li>{t('When you upgrade or downgrade your subscription plan, your remaining quota for the current period is retained until expiration')}</li>
          <li>{t('For a custom plan, please contact the administrator')}</li>
        </ul>
      </Card>
    </div>
  );
};

export default Subscription;
